import {
  Box,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Grid,
  Checkbox,
  Alert,
  Collapse,
  CircularProgress,
} from '@mui/material';
import { ArrowRight, FlowArrow, ArrowsClockwise } from '@phosphor-icons/react';
import ReusableModal from '../common/Modal';
import { useState, useEffect, memo, useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateOutboundFileMappingApi } from '../../utils/apis';

interface FieldMapping {
  sourceField: string;
  targetField: string;
  isChecked: boolean;
  color?: string;
}

interface OutboundMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: any;
  initialPhoneField?: string | null;
  onSubmit: (mappings: FieldMapping[], phoneNumberField: string | null) => void;
}

// Move helper function outside component
const hasExistingMappings = (fieldMapping: any) => {
  if (!fieldMapping) return false;
  return Object.values(fieldMapping).some(Boolean);
};

const OutboundMappingModal = ({
  isOpen,
  onClose,
  file,
  initialPhoneField,
  onSubmit,
}: OutboundMappingModalProps) => {
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [phoneNumberField, setPhoneNumberField] = useState<string | null>(null);
  const [alert, setAlert] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const queryClient = useQueryClient();

  // Update phoneNumberField when mappings change
  useEffect(() => {
    const existingPhoneNumberMapping = mappings.find(
      mapping => mapping.targetField === 'phoneNumber'
    );

    if (existingPhoneNumberMapping && !phoneNumberField) {
      setPhoneNumberField(existingPhoneNumberMapping.sourceField);
    }
  }, [mappings, phoneNumberField]);

  // Initialize mappings
  useEffect(() => {
    if (file) {
      // Get all fields from the fieldMapping object
      const fields = Object.keys(file.fieldMapping || {});

      // Find initial phone number field
      const initialPhoneNumberField = fields.find(
        field =>
          file.fieldMapping[field] === 'phoneNumber' ||
          field === initialPhoneField
      );

      setPhoneNumberField(initialPhoneNumberField || null);

      // Create mappings array from fields, preserving checked state
      setMappings(
        fields.map(field => ({
          sourceField: field,
          targetField: file.fieldMapping[field] || '',
          // Only set checked if the field has a non-null mapping
          isChecked:
            file.fieldMapping[field] !== null &&
            file.fieldMapping[field] !== '',
        }))
      );
    }
  }, [file, initialPhoneField]);

  // Memoize handlers
  const handlePhoneNumberSelection = useCallback((sourceField: string) => {
    setPhoneNumberField(sourceField);
    setMappings(prevMappings =>
      prevMappings.map(mapping => ({
        ...mapping,
        targetField:
          mapping.sourceField === sourceField
            ? 'phoneNumber'
            : mapping.targetField === 'phoneNumber'
              ? ''
              : mapping.targetField,
        isChecked: mapping.sourceField === sourceField || mapping.isChecked,
      }))
    );
  }, []);

  const handleTargetFieldChange = useCallback(
    (index: number, value: string) => {
      setMappings(prevMappings => {
        const hasPhoneNumber = prevMappings.some(
          (m, i) => i !== index && m.targetField === 'phoneNumber'
        );

        if (value === 'phoneNumber' && hasPhoneNumber) {
          return prevMappings;
        }

        const updatedMappings = [...prevMappings];
        updatedMappings[index] = {
          ...updatedMappings[index],
          targetField: value,
          isChecked: true,
        };

        if (value.toLowerCase() === 'phonenumber') {
          setPhoneNumberField(updatedMappings[index].sourceField);
        }

        return updatedMappings;
      });
    },
    []
  );

  const handleCheckboxChange = useCallback(
    (index: number, checked: boolean) => {
      setMappings(prevMappings => {
        const updatedMappings = [...prevMappings];
        updatedMappings[index] = {
          ...updatedMappings[index],
          isChecked: checked,
          targetField: checked ? updatedMappings[index].targetField : '',
        };
        return updatedMappings;
      });
    },
    []
  );

  // Add this new handler after handleCheckboxChange
  const handleMapAllFields = useCallback(() => {
    setMappings(prevMappings =>
      prevMappings.map(mapping => {
        // Skip if already checked
        if (mapping.isChecked) {
          return mapping;
        }

        // If this is the phone number field, it's already mapped
        if (mapping.sourceField === phoneNumberField) {
          return {
            ...mapping,
            isChecked: true,
            targetField: 'phoneNumber',
          };
        }

        // Map all other fields: use sourceField as targetField
        return {
          ...mapping,
          isChecked: true,
          targetField: mapping.targetField || mapping.sourceField,
        };
      })
    );
  }, [phoneNumberField]);

  const { mutate: updateMapping, isPending } = useMutation({
    mutationFn: updateOutboundFileMappingApi,
    onSuccess: async () => {
      // Refetch queries to get updated fileUrl
      await queryClient.refetchQueries({
        queryKey: ['outbound-files'],
        exact: false,
      });

      const checkedMappings = mappings
        .filter(mapping => mapping.isChecked)
        .map(mapping => ({
          ...mapping,
          targetField: mapping.targetField || mapping.sourceField,
        }));

      const selectedMapping = mappings.find(
        mapping => mapping.sourceField === phoneNumberField
      );
      const phoneNumberFieldValue =
        selectedMapping?.targetField || phoneNumberField;

      onSubmit?.(checkedMappings, phoneNumberFieldValue);
      onClose();
    },
  });

  // Memoize submit handler with proper dependencies
  const handleSubmit = useCallback(() => {
    const fieldMapping = mappings.reduce<Record<string, string | null>>(
      (acc, curr) => {
        acc[curr.sourceField] = curr.isChecked
          ? curr.targetField || curr.sourceField
          : null;
        return acc;
      },
      {}
    );

    updateMapping({
      outboundFileId: file?._id,
      fieldMapping: Object.fromEntries(
        Object.entries(fieldMapping).map(([key, value]) => [key, value ?? ''])
      ),
      phoneNumber: phoneNumberField || '',
    });
  }, [mappings, phoneNumberField, file?._id, updateMapping]);

  // Memoize the radio group options
  const radioOptions = useMemo(
    () =>
      mappings.map(mapping => (
        <FormControlLabel
          key={mapping.sourceField}
          value={mapping.sourceField}
          control={<Radio />}
          label={mapping.sourceField}
          checked={phoneNumberField === mapping.sourceField}
          onChange={() => handlePhoneNumberSelection(mapping.sourceField)}
          sx={{ mr: 1 }}
        />
      )),
    [mappings, phoneNumberField, handlePhoneNumberSelection]
  );

  // Memoize the mapping fields
  const mappingFields = useMemo(
    () =>
      mappings.map((mapping, index) => (
        <Box
          key={mapping.sourceField}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            position: 'relative',
            height: '36px',
          }}
        >
          <ArrowRight
            size={16}
            style={{
              position: 'absolute',
              left: '-16px',
              color: mapping.isChecked ? '#5A4CE6' : '#ccc',
            }}
          />
          <TextField
            fullWidth
            size="small"
            placeholder="Enter variable"
            value={mapping.targetField}
            onChange={e => handleTargetFieldChange(index, e.target.value)}
            disabled={phoneNumberField === mapping.sourceField}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '36px',
                borderColor: mapping.isChecked ? 'primary.main' : '#ccc',
                backgroundColor: mapping.isChecked ? '#F4F3FF' : 'transparent',
              },
            }}
          />
          <Checkbox
            size="small"
            checked={mapping.isChecked}
            onChange={e => handleCheckboxChange(index, e.target.checked)}
          />
        </Box>
      )),
    [mappings, handleTargetFieldChange, handleCheckboxChange, phoneNumberField]
  );

  // Clear alert when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAlert(null);
      setPhoneNumberField(null);
      setMappings([]);
    }
  }, [isOpen]);

  return (
    <ReusableModal
      open={isOpen}
      onClose={onClose}
      title="Platform-File Mapping"
      subtitle="Map your file fields to platform variables"
      width={900}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '70vh' }}>
        {/* Alert Section */}
        <Collapse in={!!alert}>
          {alert && (
            <Alert
              severity={alert.type}
              sx={{ mb: 2 }}
              onClose={() => setAlert(null)}
            >
              {alert.message}
            </Alert>
          )}
        </Collapse>

        {/* Scrollable Content Area */}
        <Box sx={{ flex: 1, overflow: 'auto', pr: 1 }}>
          {/* Radio Button Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Select the field containing contact number to initiate customer
              calls:
            </Typography>
            <RadioGroup row>{radioOptions}</RadioGroup>
          </Box>

          {/* Mapping Section */}
          <Grid container spacing={2} alignItems="flex-start">
            {/* Left Side: Fields from Document */}
            <Grid item xs={5}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Upload Doc. Field
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {mappings.map(mapping => (
                  <Box
                    key={mapping.sourceField}
                    sx={{
                      p: 1,
                      border: mapping.isChecked ? 2 : 1,
                      borderColor: mapping.isChecked ? 'primary.main' : '#ccc',
                      borderRadius: 1,
                      backgroundColor: mapping.isChecked
                        ? '#F4F3FF'
                        : '#f9f9f9',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography>{mapping.sourceField}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Right Side: Input Fields with Checkboxes for Mapping */}
            <Grid item xs={7}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1">Platform Field</Typography>
                {phoneNumberField && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleMapAllFields}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.875rem',
                    }}
                  >
                    Map All Fields
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {mappingFields}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Sticky Footer */}
        <Box
          sx={{
            mt: 2,
            pt: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            borderTop: '1px solid #e0e0e0',
            backgroundColor: 'background.paper',
          }}
        >
          <Button onClick={onClose} sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isPending}
            startIcon={
              isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : hasExistingMappings(file?.fieldMapping) ? (
                <ArrowsClockwise weight="bold" />
              ) : (
                <FlowArrow weight="bold" />
              )
            }
          >
            {isPending
              ? 'Updating...'
              : hasExistingMappings(file?.fieldMapping)
                ? 'Update Mapping'
                : 'Map File'}
          </Button>
        </Box>
      </Box>
    </ReusableModal>
  );
};

export default memo(OutboundMappingModal);
